import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Sparkles, Send, Loader2, Maximize2, Minimize2, LayoutTemplate, Star, Plus, Pencil, Copy, Archive, X, Route } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StatusPill } from "@/components/StatusPill";
import { AlertTriangle, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const CATS = ["Strategy", "Operations", "Marketing", "Creative", "Research", "Migration", "Founder Review"];

function AutoTextarea({ value, onChange, onKeyDown, expanded, placeholder }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    const max = expanded ? 9999 : 168; // ~6-8 lines collapsed
    el.style.height = Math.min(el.scrollHeight, max) + "px";
    el.style.overflowY = el.scrollHeight > max ? "auto" : "hidden";
  }, [value, expanded]);
  return (
    <textarea ref={ref} value={value} onChange={onChange} onKeyDown={onKeyDown} placeholder={placeholder}
      rows={expanded ? 16 : 2}
      className="input-dark w-full px-4 py-2.5 text-sm resize-none leading-relaxed"
      style={{ minHeight: expanded ? "60vh" : "56px" }}
      data-testid="command-composer-textarea" />
  );
}

function TemplateManager({ open, onClose, onUse }) {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("All");
  const [editing, setEditing] = useState(null); // null | {} (new) | template
  const [form, setForm] = useState({ title: "", category: "Operations", body: "", scope: "COMPANY", project_code: "" });

  const load = () => api.templates().then(setItems);
  useEffect(() => { if (open) load(); }, [open]);

  const startNew = () => { setForm({ title: "", category: "Operations", body: "", scope: "COMPANY", project_code: "" }); setEditing({}); };
  const startEdit = (tpl) => { setForm({ title: tpl.title, category: tpl.category, body: tpl.body, scope: tpl.scope || "COMPANY", project_code: tpl.project_code || "" }); setEditing(tpl); };

  const save = async () => {
    if (!form.title.trim() || !form.body.trim()) return toast.error(t("cmp.needTitleBody"));
    try {
      if (editing && editing.id) await api.updateTemplate(editing.id, form);
      else await api.createTemplate(form);
      toast.success(t("cmp.toastSaved")); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const dup = async (id) => { await api.duplicateTemplate(id); toast.success(t("cmp.toastDup")); load(); };
  const arch = async (id) => { await api.archiveTemplate(id); toast.success(t("cmp.toastArchived")); load(); };
  const fav = async (id) => { await api.favoriteTemplate(id); toast.success(t("cmp.toastFav")); load(); };

  const filtered = items.filter((x) => cat === "All" || x.category === cat);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && (setEditing(null), onClose())}>
      <DialogContent className="bg-[#0F1626] border-[#1E293B] text-[#E2E8F0] max-w-3xl max-h-[88vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-white flex items-center gap-2"><LayoutTemplate size={17} className="text-[#2563EB]" /> {t("cmp.pickerTitle")}</DialogTitle></DialogHeader>

        {!editing ? (
          <>
            <p className="text-xs text-[#64748B]">{t("cmp.pickerHint")}</p>
            <div className="flex flex-wrap gap-1.5 items-center">
              {["All", ...CATS].map((c) => (
                <button key={c} onClick={() => setCat(c)} className={`h-7 px-3 rounded-full text-[11px] ${cat === c ? "bg-[#2563EB] text-white" : "bg-[#0B0F19] text-[#94A3B8] border border-[#1E293B]"}`} data-testid={`tpl-cat-${c.replace(/ /g, "-")}`}>{c === "All" ? t("cmp.allCats") : c}</button>
              ))}
              <button onClick={startNew} className="btn-ghost h-7 px-3 text-[11px] flex items-center gap-1 ml-auto" data-testid="tpl-new"><Plus size={13} /> {t("cmp.newTemplate")}</button>
            </div>
            <div className="space-y-2 mt-1">
              {filtered.map((tpl) => (
                <div key={tpl.id} className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-3" data-testid={`tpl-item-${tpl.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      {tpl.favorite && <Star size={13} className="text-[#FCD34D] fill-[#FCD34D] shrink-0" />}
                      <span className="text-white text-sm font-medium truncate">{tpl.title}</span>
                      <span className="m-pill text-[10px]" style={{ color: "#60A5FA", borderColor: "#1E293B", background: "#0B0F19" }}>{tpl.category}</span>
                      <span className="text-[9px] text-[#475569] font-mono">{tpl.scope}{tpl.scope === "PROJECT" && tpl.project_code ? `·${tpl.project_code}` : ""} {t("cmp.versionN", { n: tpl.version })}</span>
                    </div>
                    <button onClick={() => onUse(tpl)} className="btn-primary h-7 px-3 text-[11px] shrink-0" data-testid={`tpl-use-${tpl.id}`}>{t("cmp.useTemplate")}</button>
                  </div>
                  <p className="text-[11px] text-[#94A3B8] mt-1.5 line-clamp-2">{tpl.body}</p>
                  <div className="flex items-center gap-3 mt-2 text-[#64748B]">
                    <button onClick={() => fav(tpl.id)} className="hover:text-[#FCD34D]" title={t("cmp.favorite")} data-testid={`tpl-fav-${tpl.id}`}><Star size={13} /></button>
                    <button onClick={() => startEdit(tpl)} className="hover:text-white" title={t("cmp.edit")} data-testid={`tpl-edit-${tpl.id}`}><Pencil size={13} /></button>
                    <button onClick={() => dup(tpl.id)} className="hover:text-white" title={t("cmp.duplicate")} data-testid={`tpl-dup-${tpl.id}`}><Copy size={13} /></button>
                    <button onClick={() => arch(tpl.id)} className="hover:text-[#FCA5A5]" title={t("cmp.archive")} data-testid={`tpl-arch-${tpl.id}`}><Archive size={13} /></button>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="space-y-3">
            <p className="mono-label">{editing.id ? t("cmp.editTitle") : t("cmp.createTitle")}</p>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={t("cmp.title")} className="input-dark w-full h-10 px-3 text-sm" data-testid="tpl-form-title" />
            <div className="flex gap-2">
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="input-dark h-10 px-2 text-sm flex-1" data-testid="tpl-form-category">{CATS.map((c) => <option key={c} value={c}>{c}</option>)}</select>
              <select value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })} className="input-dark h-10 px-2 text-sm" data-testid="tpl-form-scope"><option value="COMPANY">{t("cmp.scopeCompany")}</option><option value="PROJECT">{t("cmp.scopeProject")}</option></select>
              {form.scope === "PROJECT" && <input value={form.project_code} onChange={(e) => setForm({ ...form, project_code: e.target.value })} placeholder="P001" className="input-dark h-10 px-3 text-sm w-24" data-testid="tpl-form-project" />}
            </div>
            <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={6} placeholder={t("cmp.body")} className="input-dark w-full p-3 text-sm resize-none" data-testid="tpl-form-body" />
            {editing.id && editing.versions?.length > 1 && (
              <details className="text-xs text-[#64748B]"><summary className="cursor-pointer">{t("cmp.versions")} ({editing.versions.length})</summary>
                <div className="mt-2 space-y-1">{editing.versions.slice().reverse().map((v) => <div key={v.version} className="font-mono text-[10px]">{t("cmp.versionN", { n: v.version })} · {v.by} · {(v.at || "").slice(0, 10)}</div>)}</div>
              </details>
            )}
            <div className="flex gap-2">
              <button onClick={save} className="btn-primary h-10 px-5 text-sm" data-testid="tpl-form-save">{t("cmp.save")}</button>
              <button onClick={() => setEditing(null)} className="btn-ghost h-10 px-4 text-sm flex items-center gap-1"><X size={14} /></button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function CommandComposer() {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [sugProject, setSugProject] = useState(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [projectOptions, setProjectOptions] = useState([]);
  const [selectedProjectCode, setSelectedProjectCode] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useI18n();
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  const segs = location.pathname.split("/").filter(Boolean);
  const page = segs[0] || "home";
  const ctxProject = page === "projects" && segs[1] ? segs[1] : null;
  const activeProject = ctxProject || selectedProjectCode || undefined;
  const isStatusQuestion = /(status|progress|progres|sampai mana|sampai di mana|sejauh mana|perkembangan|kabar|gimana|bagaimana|sudah sampai|udah sampai)/i.test(text) && !/^(perbaiki|buat|audit|ubah|implementasi|deploy|build|kembangkan|uji|jalankan|lanjutkan|fix|test)( |$)/i.test(text.trim());

  useEffect(() => {
    let alive = true;
    api.projects().then((items) => { if (alive) setProjectOptions(Array.isArray(items) ? items : []); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    api.templateSuggestions(activeProject, page)
      .then((r) => { if (alive) { setSuggestions(r.suggestions || []); setSugProject(r.project || null); } })
      .catch(() => {});
    return () => { alive = false; };
  }, [location.pathname, selectedProjectCode]);

  // Read-only routing preview: never creates a mission, task, or external action.
  const inspectRoute = async () => {
    if (!text.trim() || previewLoading || loading) return;
    if (isStatusQuestion) { await submit(); return; } // Status questions need answers, never a routing preview.
    setPreviewLoading(true);
    try {
      const route = await api.resolveRouting(text.trim(), activeProject);
      setPreview(route);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Belum bisa mengenali project. Pilih project dari Portofolio terlebih dahulu.");
    } finally { setPreviewLoading(false); }
  };

  const submit = async () => {
    if (!text.trim() || loading) return;
    setLoading(true); setExpanded(false); setPreview(null);
    try {
      const idem = (crypto.randomUUID && crypto.randomUUID()) || `${Date.now()}-${Math.random()}`;
      const { job_id } = await api.command(text.trim(), activeProject, idem, selectedTemplateId || undefined);
      setText(""); setSelectedTemplateId(null);
      let job = null;
      for (let i = 0; i < 90; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        job = await api.commandJob(job_id);
        if (!mounted.current) return;
        if (job.status === "COMPLETED" || job.status === "FAILED") break;
      }
      if (!job || job.status !== "COMPLETED") { toast.error(job?.error ? t("layout.toastCmdFailed") : t("layout.toastStillRunning")); return; }
      const res = job.result; setResult(res);
      if (res.type === "MISSION") toast.success(t("layout.toastMission"));
      else if (res.type === "STATUS") toast.success(t("layout.toastStatus"));
      else if (res.type === "PROJECT_REQUIRED") toast.warning("Pilih project sebelum command dijalankan.");
      else if (res.type === "CROSS_PROJECT_BLOCK") toast.warning(t("layout.toastCross"));
      else if (res.type === "MIGRATION_GATE_CONFIRM") toast.info(t("gate.toastConfirm"));
      else if (res.type === "MIGRATION_GATE_STATUS") toast.success(t("gate.toastOpened"));
      else if (res.type === "MIGRATION_GATE_BLOCKED") toast.warning(t("gate.toastBlocked"));
      else if (res.type === "MIGRATION_START_STAGED") toast.info(t("gate.toastStaged"));
      else if (res.type === "MIGRATION_BATCH_INVENTORY") toast.success(t("gate.batchReady"));
      else toast.info(t("layout.toastReceived"));
    } catch (e) { toast.error(t("layout.toastCmdFailedRetry")); } finally { setLoading(false); }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); }
  };

  const fillTemplate = (tpl) => {
    let body = tpl.body || "";
    if (sugProject) {
      const label = `${sugProject.code} ${sugProject.name}`;
      body = body.split("[PROJECT / COMPANY]").join(label).split("[PROJECT]").join(label);
    }
    setText(body); setPreview(null); setSelectedTemplateId(tpl.id); toast.info(t("cmp.toastFilled"));
  };
  const useTemplate = (tpl) => { fillTemplate(tpl); setPickerOpen(false); setExpanded(true); };
  const applySuggestion = (tpl) => { fillTemplate(tpl); };

  const renderRunButton = (full) => (
    <div className={`flex gap-2 ${full ? "justify-end" : "shrink-0 self-end"}`}>
      <button onClick={inspectRoute} disabled={loading || previewLoading || !text.trim()}
        className="btn-ghost flex items-center gap-2 h-11 px-3 text-xs disabled:opacity-50"
        data-testid="command-preview-route" title="Pratinjau rute, tanpa membuat tugas atau menjalankan alat">
        {previewLoading ? <Loader2 size={15} className="animate-spin" /> : <Route size={15} />}
        <span className="hidden sm:inline">{isStatusQuestion ? "Lihat Progres" : "Cek Rute"}</span>
      </button>
      <button onClick={submit} disabled={loading || previewLoading || !text.trim()}
        className={`btn-primary flex items-center gap-2 h-11 text-sm disabled:opacity-60 ${full ? "px-6" : "px-4"}`}
        data-testid="command-bar-submit">
        {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
        <span>{loading ? t("layout.running") : isStatusQuestion ? "Tanya Progres" : "Kirim Perintah"}</span>
      </button>
    </div>
  );

  const renderComposer = (full) => (
    <div className={full ? "flex flex-col gap-3 h-full" : "flex items-end gap-3"}>
      <div className="flex-1 w-full min-w-0">
        {suggestions.length > 0 && (
          <div className="flex items-center gap-1.5 mb-1.5 overflow-x-auto" data-testid="template-suggestions">
            <span className="text-[10px] text-[#64748B] shrink-0 uppercase tracking-wider">{t("cmp.suggestions")}</span>
            {suggestions.map((s) => (
              <button key={s.id} onClick={() => applySuggestion(s)} title={`${s.category} · ${s.reason}`}
                className="h-6 px-2.5 rounded-full text-[10px] shrink-0 bg-[#0B0F19] border border-[#1E293B] text-[#94A3B8] hover:border-[#2563EB] hover:text-white transition-colors flex items-center gap-1"
                data-testid={`suggestion-chip-${s.id}`}>
                {s.favorite && <Star size={9} className="text-[#FCD34D] fill-[#FCD34D]" />}{s.title}
              </button>
            ))}
          </div>
        )}
        <div className="flex items-center gap-2 mb-1.5 flex-wrap">
          <Sparkles size={15} className="text-[#2563EB]" />
          <label className="flex items-center gap-1.5 text-[11px] text-[#94A3B8]" data-testid="command-project-scope">
            <span>Project:</span>
            <select className="input-dark h-7 max-w-[220px] px-2 text-[11px]"
              aria-label="Pilih project untuk perintah MASANDI"
              value={activeProject || ""} disabled={!!ctxProject}
              onChange={(e) => { setSelectedProjectCode(e.target.value); setSelectedTemplateId(null); setPreview(null); }}
              data-testid="command-project-select">
              <option value="">Deteksi dari perintah</option>
              {projectOptions.map((project) => <option key={project.code} value={project.code}>
                {project.code} · {project.name}
              </option>)}
            </select>
          </label>
          <button onClick={() => setPickerOpen(true)} className="btn-ghost h-7 px-2.5 text-[11px] flex items-center gap-1.5" data-testid="command-templates-button"><LayoutTemplate size={13} /> {t("cmp.templates")}</button>
          <button onClick={() => setExpanded((v) => !v)} className="btn-ghost h-7 px-2 text-[11px] flex items-center gap-1 ml-auto" data-testid="command-expand-toggle">
            {full ? <><Minimize2 size={13} /> {t("cmp.collapse")}</> : <><Maximize2 size={13} /> {t("cmp.expand")}</>}
          </button>
        </div>
        <AutoTextarea value={text} onChange={(e) => { setText(e.target.value); setPreview(null); if (!e.target.value) setSelectedTemplateId(null); }} onKeyDown={onKeyDown} expanded={full} placeholder={t("layout.commandPlaceholder")} />
      </div>
      {full ? <div className="flex justify-end">{renderRunButton(true)}</div> : renderRunButton(false)}
    </div>
  );

  return (
    <>
      <div className="fixed bottom-0 left-0 right-0 lg:left-60 z-30 glass border-t border-[#1E293B] px-4 sm:px-6 py-3">
        <div className="max-w-4xl mx-auto">{renderComposer(false)}</div>
      </div>

      {/* Fullscreen composer */}
      <Dialog open={expanded} onOpenChange={setExpanded}>
        <DialogContent className="bg-[#0F1626] border-[#1E293B] text-[#E2E8F0] max-w-3xl h-[80vh] flex flex-col">
          <DialogHeader><DialogTitle className="font-display text-white flex items-center gap-2"><Sparkles size={17} className="text-[#2563EB]" /> {t("layout.execute")}</DialogTitle></DialogHeader>
          <div className="flex-1 min-h-0">{renderComposer(true)}</div>
        </DialogContent>
      </Dialog>

      <TemplateManager open={pickerOpen} onClose={() => setPickerOpen(false)} onUse={useTemplate} />

      {/* Advisory route preview — not a command submission. */}
      <Dialog open={!!preview} onOpenChange={(open) => !open && setPreview(null)}>
        <DialogContent className="bg-[#0F1626] border-[#1E293B] text-[#E2E8F0] max-w-2xl">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Route size={18} className="text-[#38BDF8]" /> Cek rute perintah</DialogTitle></DialogHeader>
          {preview && <div className="space-y-3 text-sm" data-testid="command-route-preview">
            <p className="text-[#FBBF24] text-xs">Pratinjau saja. Belum ada misi, tugas, pembayaran, atau tindakan eksternal yang dijalankan.</p>
            <div className="grid sm:grid-cols-2 gap-2">
              {[
                ["PROJECT", preview.project?.name || "Belum dipilih"],
                ["KLASIFIKASI", preview.classification],
                ["FUNGSI", preview.routing_plan?.operating_module_name],
                ["EKSEKUTOR", preview.routing_plan?.selected_execution_tool],
                ["JALUR", preview.routing_plan?.dispatch_behavior === "TRACK_ONLY" ? "Pencatatan / handoff, belum eksekusi otomatis" : "Mission pipeline internal"],
                ["APPROVAL", preview.routing_plan?.approval_required ? "Perlu keputusan Founder" : "Tidak terdeteksi batas approval material"],
              ].map(([label,value]) => <div key={label} className="rounded-lg border border-[#254266] p-3">
                <p className="mono-label">{label}</p><p className="mt-1 text-white">{value || "—"}</p>
              </div>)}
            </div>
            {preview.routing_plan?.unresolved?.length > 0 && <p className="text-[#FBBF24] text-xs">
              Perlu diselesaikan: {preview.routing_plan.unresolved.join(", ")}
            </p>}
            <p className="text-xs text-[#94A3B8]">{preview.routing_plan?.next_action}</p>
            <div className="flex justify-end gap-2">
              <button className="btn-ghost h-10 px-4" onClick={() => setPreview(null)}>Kembali</button>
              <button className="btn-primary h-10 px-4" onClick={submit} disabled={loading}>Kirim Perintah</button>
            </div>
          </div>}
        </DialogContent>
      </Dialog>

      {/* Orchestrator response */}
      <Dialog open={!!result} onOpenChange={(o) => !o && setResult(null)}>
        <DialogContent className="bg-[#0F1626] border-[#1E293B] text-[#E2E8F0] max-w-2xl max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-white flex items-center gap-2"><Sparkles size={18} className="text-[#2563EB]" /> {result?.type === "STATUS" ? "Progres Project" : result?.type === "ROUTED_TASK" ? "Perintah diterima" : result?.type === "INFO" ? "Informasi Perintah" : t("layout.orchestratorResponse")}</DialogTitle></DialogHeader>
          {result && (
            <div className="space-y-4 text-sm">
              {result.type === "MISSION" && <div className="flex flex-wrap gap-2 items-center">
                <span className="mono-label">{t("layout.classified")}</span>
                <StatusPill status="PLANNING" label={result.classification} />
                <span className="mono-label ml-2">{t("layout.project")}</span>
                <span className="text-white font-medium">{result.project?.code} · {result.project?.name}</span>
              </div>}
              {result.type === "MISSION" && <p className="text-[#94A3B8] text-xs">{t("layout.resolution", { v: result.resolution })}</p>}

              {result.type === "MISSION" && result.routing_plan && (
                <div className="m-card p-4 space-y-3" data-testid="command-routing-plan">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <p className="mono-label flex items-center gap-2"><Route size={14} className="text-[#38BDF8]" /> ROUTING LOGIC V1</p>
                    <span className="text-[9px] text-[#64748B] font-mono">{result.routing_plan.execution_binding}</span>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-2 text-xs">
                    {[
                      ["MODULE", result.routing_plan.operating_module_name || result.routing_plan.operating_module],
                      ["PLAYBOOK", result.routing_plan.playbook_name || result.routing_plan.playbook || "—"],
                      ["WORK TYPE", result.routing_plan.work_type],
                      ["FUNCTIONAL OWNER", result.routing_plan.functional_owner],
                      ["EXECUTION TOOL", result.routing_plan.selected_execution_tool],
                      ["APPROVAL", result.routing_plan.approval_required ? "REQUIRED" : "NOT REQUIRED BY MATERIAL BOUNDARY"],
                    ].map(([k, v]) => (
                      <div key={k} className="bg-[#0B0F19] border border-[#1E293B] rounded-lg p-2.5">
                        <p className="mono-label">{k}</p>
                        <p className="text-white mt-1">{v}</p>
                      </div>
                    ))}
                  </div>
                  {result.routing_plan.approval_boundaries?.length > 0 && (
                    <p className="text-[11px] text-[#FCA5A5]">
                      APPROVAL BOUNDARY · {result.routing_plan.approval_boundaries.join(" · ")}
                    </p>
                  )}
                  {result.routing_plan.unresolved?.length > 0 && (
                    <p className="text-[11px] text-[#FBBF24]">
                      UNRESOLVED · {result.routing_plan.unresolved.join(" · ")}
                    </p>
                  )}
                  {result.routing_plan.next_action && (
                    <p className="text-[11px] text-[#CBD5E1]">
                      NEXT · {result.routing_plan.next_action}
                    </p>
                  )}
                  <p className="text-[10px] text-[#64748B]">
                    Routing ini menentukan jalur kerja dan governance. V1 tidak otomatis menjalankan Work/Codex, deploy, migration, atau external action.
                  </p>
                </div>
              )}

              {result.type === "ROUTED_TASK" && (
                <div className="space-y-3" data-testid="command-routed-task">
                  <div className="m-card p-4 space-y-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="text-white font-semibold text-lg">Pekerjaan dicatat di MASANDI</p>
                      <StatusPill status={result.task?.status} />
                    </div>
                    <p className="text-[#E2E8F0] text-sm">{result.task?.title || "Perintah Founder"}</p>
                    <p className="text-[#94A3B8] text-xs">
                      Project: {result.project?.name} · Pekerjaan sudah dibuat sebagai task.
                      {result.task?.status === "APPROVAL_REQUIRED"
                        ? " Perlu keputusan melalui menu Persetujuan sebelum dijalankan."
                        : " Belum dieksekusi oleh Work/Codex atau operator eksternal."}
                    </p>
                    {result.routing_plan?.unresolved?.includes("canonical_repo_missing") && (
                      <p className="text-xs text-[#FBBF24]" data-testid="task-repo-warning">
                        Sumber kode project belum ditautkan ke repository MASANDI. Task tetap tersimpan,
                        tetapi perbaikan landing page belum dapat dijalankan otomatis.
                      </p>
                    )}
                    <p className="text-xs text-[#94A3B8]">
                      Langkah berikutnya: buka Eksekusi untuk melihat status dan handoff.
                      Jangan menganggap pekerjaan selesai sebelum ada bukti hasil dan review.
                    </p>
                  </div>
                  <button onClick={() => { navigate("/jobs"); setResult(null); }}
                    className="btn-primary w-full h-10 text-sm" data-testid="command-open-routed-task">
                    Lihat Task di Eksekusi
                  </button>
                </div>
              )}

              {result.type === "MISSION" && (
                <div className="space-y-3">
                  <div className="m-card p-4"><p className="text-white font-semibold">{result.mission?.title}</p><div className="flex items-center gap-2 mt-2"><StatusPill status={result.mission?.status} /><span className="text-xs text-[#64748B] font-mono">cost ~${result.cost}</span></div></div>
                  <button onClick={() => { navigate(`/missions/${result.mission.id}`); setResult(null); }} className="btn-primary w-full h-10 text-sm" data-testid="command-open-mission">{t("layout.openMissionReview")}</button>
                </div>
              )}

              {result.type === "STATUS" && (() => {
                const progress = result.status.founder_progress || {};
                return <div className="space-y-4" data-testid="founder-project-progress-answer">
                  <div className="m-card p-4 space-y-2">
                    <p className="text-white font-semibold text-lg">{result.project?.name} — sampai mana progresnya?</p>
                    <p className="text-[#CBD5E1] leading-relaxed">{progress.headline || "Ringkasan pekerjaan belum tersedia. Periksa catatan project."}</p>
                    <p className="text-xs text-[#FBBF24]">{progress.external_verification_note || "Belum terverifikasi terhadap aplikasi bisnis live."}</p>
                  </div>
                  {progress.live_evidence && (
                    <section className="m-card p-4 space-y-3" data-testid="luri-live-operational-evidence">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="text-white font-semibold">Pemeriksaan langsung lurisala.com</p>
                        <span className="text-[11px] text-[#94A3B8]">
                          {progress.live_evidence.checked_at ? new Date(progress.live_evidence.checked_at).toLocaleString("id-ID") : "Belum tercatat"}
                        </span>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-2 text-xs">
                        {[
                          ["Website", progress.live_evidence.storefront === "ONLINE" ? "Online" : "Belum berhasil diperiksa"],
                          ["Checkout", progress.live_evidence.checkout === "CONFIGURED" ? "Aktif menurut konfigurasi" : "Belum terkonfirmasi aktif"],
                          ["DOKU", progress.live_evidence.payment_configuration === "CONFIGURED" ? "Kredensial merchant dikonfigurasi" : "Belum terkonfirmasi"],
                          ["Webhook pembayaran", progress.live_evidence.successful_signed_paid_webhook === "OBSERVED" ? "Notifikasi sukses tercatat, perlu rekonsiliasi" : progress.live_evidence.successful_signed_paid_webhook === "NOT_OBSERVED" ? "Belum ada notifikasi pembayaran sukses" : "Belum bisa diverifikasi"],
                          ["Biteship", progress.live_evidence.shipping_configuration === "CONFIGURED" ? "Konfigurasi pengiriman aktif" : "Belum terkonfirmasi"],
                          ["Tarif pengiriman", progress.live_evidence.shipping_quotes_configuration === "CONFIGURED" ? "Endpoint tarif diaktifkan; tarif nyata belum diuji" : "Belum terkonfirmasi"],
                        ].map(([title, value]) => (
                          <div key={title} className="rounded-lg border border-[#274268] p-3">
                            <p className="text-[#94A3B8]">{title}</p>
                            <p className="text-white font-medium mt-1">{value}</p>
                          </div>
                        ))}
                      </div>
                      {(progress.live_evidence.active_package_days?.length > 0 || progress.live_evidence.disabled_package_days?.length > 0) && (
                        <p className="text-xs text-[#CBD5E1]">Paket aktif: {(progress.live_evidence.active_package_days || []).join(", ") || "—"} hari.
                          Paket nonaktif: {(progress.live_evidence.disabled_package_days || []).join(", ") || "—"} hari.</p>
                      )}
                      <p className="text-xs text-[#FBBF24]">
                        Tidak ada bukti pembayaran lunas atau ongkir nyata dari pemeriksaan publik ini.
                        Status live website tidak berarti transaksi end-to-end telah lolos.
                      </p>
                    </section>
                  )}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                    {[
                      ["Tahap", progress.project_stage || result.status.stage || "Belum dicatat"],
                      ["Konteks disetujui", progress.approved_contexts ?? "—"],
                      ["Task aktif", progress.active_task_count ?? "—"],
                      ["Task selesai tercatat", progress.done_task_count ?? "—"],
                    ].map(([label, value]) => (
                      <div className="m-card p-3" key={label}><p className="mono-label">{label}</p>
                        <p className="text-white mt-2 font-semibold">{value}</p></div>
                    ))}
                  </div>
                  <section className="space-y-2">
                    <p className="text-white font-semibold">Pekerjaan saat ini</p>
                    {progress.current_tasks?.length ? progress.current_tasks.map((task, i) =>
                      <div className="rounded-lg border border-[#274268] p-3" key={i}>
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <p className="text-white font-medium">{task.title}</p>
                          <span className="text-[#FBBF24] text-xs font-semibold">{task.status}</span>
                        </div>
                        <p className="text-xs text-[#94A3B8] mt-1">{task.status === "READY"
                          ? "Tugas tercatat, belum mulai dieksekusi." : task.status === "REVIEW"
                          ? "Hasil perlu diverifikasi Founder." : task.next_action || "Lihat detail di Eksekusi."}</p>
                      </div>) :
                      <p className="text-[#94A3B8] text-xs">Belum ada pekerjaan aktif yang tercatat.</p>}
                  </section>
                  <section className="space-y-2">
                    <p className="text-white font-semibold">Langkah berikutnya</p>
                    {progress.next_actions?.length ? progress.next_actions.map((action, i) =>
                      <p className="text-[#CBD5E1] text-sm" key={i}>{i + 1}. {action}</p>) :
                      <p className="text-[#94A3B8] text-xs">Perlu memperbarui bukti progres project terlebih dahulu.</p>}
                  </section>
                  <div className="border-t border-[#274268] pt-3 space-y-1">
                    <p className="text-xs text-[#94A3B8]">Sumber: {progress.source_label || "Catatan MASANDI OS"}</p>
                    <p className="text-xs text-[#94A3B8]">Data terakhir: {progress.last_recorded_at ? new Date(progress.last_recorded_at).toLocaleString("id-ID") : "belum tercatat"} · Status aplikasi eksternal: belum diverifikasi</p>
                  </div>
                  <div className="flex flex-wrap justify-end gap-2">
                    <button className="btn-ghost h-9 px-4" onClick={() => { navigate(`/continuity?project=${result.project?.code}`); setResult(null); }}>Lihat Konteks</button>
                    <button className="btn-primary h-9 px-4" onClick={() => { navigate("/jobs"); setResult(null); }}>Lihat Eksekusi</button>
                  </div>
                </div>;
              })()}

              {result.type === "INFO" && <p className="text-[#E2E8F0]">{result.message}</p>}

              {result.type === "PROJECT_REQUIRED" && (
                <div className="space-y-3" data-testid="command-project-required">
                  <div className="flex items-start gap-2 text-sm text-[#FCD34D] bg-[#422006] border border-[#A16207] rounded-lg p-4">
                    <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                    <div>
                      <p className="font-semibold text-white">PROJECT REQUIRED</p>
                      <p className="mt-1">{result.message}</p>
                      <p className="text-xs text-[#FDE68A] mt-2">{result.resolution}</p>
                    </div>
                  </div>
                  <button onClick={() => { navigate("/projects"); setResult(null); }}
                    className="btn-primary w-full h-10 text-sm">
                    Buka Portfolio
                  </button>
                </div>
              )}

              {result.type === "CROSS_PROJECT_BLOCK" && (
                <div className="space-y-3">
                  <div className="flex items-start gap-2 text-sm text-[#F0ABFC] bg-[#3B0764] border border-[#9333EA] rounded-lg p-4" data-testid="cross-project-block"><AlertTriangle size={16} className="mt-0.5 shrink-0" /><div><p className="font-semibold text-white">CROSS_PROJECT_BLOCK</p><p className="mt-1">{result.message}</p><p className="text-xs text-[#c4b5fd] mt-2">{result.project?.code} → {result.target_project?.code}. {t("layout.noDataAccessed")}</p></div></div>
                  <button onClick={() => { navigate("/approvals"); setResult(null); }} className="btn-primary w-full h-10 text-sm" data-testid="command-open-approvals">{t("layout.reviewAuthRequest")}</button>
                </div>
              )}

              {result.type === "MIGRATION_GATE_CONFIRM" && (
                <div className="space-y-3">
                  <div className="m-card p-4" style={{ borderColor: "#2563EB" }}><p className="text-white font-semibold">{result.message}</p><p className="text-xs text-[#64748B] mt-1 font-mono">{t("gate.readiness")}: {result.readiness?.status} · {result.readiness?.checked_at}</p></div>
                  <button onClick={async () => { try { const r = await api.migrationGateConfirm(); setResult({ ...result, type: "MIGRATION_GATE_STATUS", gate_status: r.gate_status }); toast.success(t("gate.toastOpened")); } catch (e) { toast.error(e.response?.data?.detail || t("gate.toastBlocked")); } }} className="btn-primary w-full h-10 text-sm" data-testid="command-gate-confirm">{t("gate.confirmCta")}</button>
                </div>
              )}

              {result.type === "MIGRATION_GATE_BLOCKED" && (
                <div className="space-y-2" data-testid="gate-blocked">
                  <div className="flex items-start gap-2 text-sm text-[#FDBA74] bg-[#431407] border border-[#EA580C] rounded-lg p-4"><AlertTriangle size={16} className="mt-0.5 shrink-0" /><div><p className="font-semibold text-white">{t("gate.blockedTitle")}</p><p className="text-xs mt-1">{t("gate.blockedNote")}</p></div></div>
                  <ul className="text-xs text-[#CBD5E1] space-y-1 pl-2">{(result.blockers || []).map((b, i) => <li key={i} className="font-mono">• {b}</li>)}</ul>
                </div>
              )}

              {result.type === "MIGRATION_GATE_STATUS" && (
                <div className="space-y-3">
                  <div className="m-card p-4" style={{ borderColor: "#10B981" }} data-testid="gate-open-status"><p className="text-white font-semibold flex items-center gap-2"><ShieldCheck size={15} className="text-[#34D399]" /> {t("gate.openedTitle")}</p><p className="text-xs text-[#64748B] mt-1">{t("gate.openedNote")}</p></div>
                  <button onClick={async () => { try { const r = await api.migrationGateStart(); setResult({ ...result, type: "MIGRATION_START_STAGED", message: r.message }); toast.info(t("gate.toastStaged")); } catch (e) { toast.error(e.response?.data?.detail || t("gate.toastBlocked")); } }} className="btn-primary w-full h-10 text-sm" data-testid="command-start-migration">{t("gate.startCta")}</button>
                </div>
              )}

              {result.type === "MIGRATION_START_STAGED" && (
                <div className="m-card p-4" style={{ borderColor: "#38BDF8" }} data-testid="migration-staged"><p className="text-[#E2E8F0] text-sm">{result.message}</p></div>
              )}

              {result.type === "MIGRATION_BATCH_INVENTORY" && (
                <div className="space-y-3" data-testid="migration-batch-inventory">
                  <div className="m-card p-4" style={{ borderColor: "#38BDF8" }}>
                    <p className="text-white font-semibold">{result.batch?.title}</p>
                    <p className="text-xs text-[#64748B] mt-1">{result.message}</p>
                    <div className="flex flex-wrap gap-1.5 mt-2.5">
                      {Object.entries(result.batch?.summary || {}).map(([k, v]) => (
                        <span key={k} className="m-pill text-[10px]" data-testid={`batch-summary-${k}`}>{k}: {v}</span>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-1.5 max-h-[46vh] overflow-y-auto pr-1">
                    {(result.batch?.items || []).map((it) => (
                      <div key={it.key} className="bg-[#0B0F19] border border-[#1E293B] rounded-lg p-2.5 flex items-center justify-between gap-2" data-testid={`batch-item-${it.key}`}>
                        <div className="min-w-0">
                          <p className="text-white text-xs font-medium">{it.label}</p>
                          <p className="text-[10px] text-[#64748B] font-mono truncate">{it.source ? `${it.source_type} · ${it.source}` : `→ ${it.target_collection}`}</p>
                        </div>
                        <StatusPill status={it.status === "AVAILABLE" ? "ACTIVE" : it.status === "NEEDS_REVIEW" ? "REVIEW" : it.status === "MISSING" ? "BLOCKED" : "PENDING"} label={it.status} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
